import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useFeathers, isPermitted, usePrevious, loadPaginatedFrontend, asyncPoolAll } from '../../app/util';
import { useSelector } from 'react-redux';
import {
  Link,
  Redirect,
  useRouteMatch,
  useParams,
  useHistory,
  useLocation,
} from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import {
  CTAButton,
  RacePreviewTile,
  PaginationStatus,
  LoadingSpinner,
  Modal,
  CreateRaceModal,
  FilterButton,
  DataDetailToolbar,
  DownloadData
} from '../../components';
import styled from 'styled-components';
import qs from 'qs';
import moment from 'moment';
import ArrowUp from '@mui/icons-material/ArrowUpward';
import ArrowDown from '@mui/icons-material/ArrowDownward';
import IconButton from "@mui/material/IconButton";
import MoreVert from '@mui/icons-material/MoreVert';
import { raceDistrictDisplayText } from '../../app/util/race-display';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;

  h1 {
    font-size: 28px;
    margin: 0;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 12px 0;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`;

const RaceSearchSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-left: auto;
  min-width: 240px;
  max-width: 240px;
  width: min(240px, 100%);
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 8px;
  border: 1px #EEEEEE solid;
  overflow: hidden;
`;

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    marginBottom: 20px;
  }
`;

const defaultRaceFilters = () => ({
  search: '',
  county: null,
  city: null,
  office: null,
  coveragePlan: null,
  uncontestedDetailed: null,
  party: null,
  priorityLevel: null,
  workflow: null,
  runoff: null,
});

const PROCESS_POLL_INTERVAL_MS = 3000;
const PROCESS_MAX_DURATION_MS = 10 * 60 * 1000;
const EXPORT_RACE_QUERY_CHUNK_SIZE = 20;

function parseRaceFiltersFromQuery(query) {
  const f = defaultRaceFilters();
  if (typeof query.search === 'string' && query.search.length > 0) {
    f.search = query.search;
  }
  let county = query.county ? String(query.county) : null;
  let city = query.city ? String(query.city) : null;
  if (county && city) county = null;
  f.county = county;
  f.city = city;
  if (query.office) f.office = String(query.office);
  if (query.coveragePlan) f.coveragePlan = String(query.coveragePlan);
  if (query.uncontestedDetailed) f.uncontestedDetailed = String(query.uncontestedDetailed);
  if (query.party) f.party = String(query.party);
  if (query.priorityLevel) f.priorityLevel = String(query.priorityLevel);
  if (query.workflow) f.workflow = String(query.workflow);
  if (query.runoff === 'true' || query.runoff === true) f.runoff = true;
  else if (query.runoff === 'false' || query.runoff === false) f.runoff = false;
  return f;
}

function raceFiltersToQueryParams(filters) {
  const q = {};
  if (filters.search) q.search = filters.search;
  if (filters.county) q.county = filters.county;
  if (filters.city) q.city = filters.city;
  if (filters.office) q.office = filters.office;
  if (filters.coveragePlan) q.coveragePlan = filters.coveragePlan;
  if (filters.uncontestedDetailed) q.uncontestedDetailed = filters.uncontestedDetailed;
  if (filters.party) q.party = filters.party;
  if (filters.priorityLevel) q.priorityLevel = filters.priorityLevel;
  if (filters.workflow) q.workflow = filters.workflow;
  if (filters.runoff === true) q.runoff = 'true';
  if (filters.runoff === false) q.runoff = 'false';
  return q;
}

function buildRacesUrlQuery({ page, sortFilters, filters }) {
  const q = raceFiltersToQueryParams(filters);
  if (page) q.page = String(page);
  if (sortFilters?.length) q.sortBy = JSON.stringify(sortFilters);
  return qs.stringify(q);
}

const Races = () => {
  const feathers = useFeathers();
  const election = useParams().key;
  const route = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => state.user);
  const electionData = useSelector(state => state?.elections?.byKey[election])
  const isPartisan = electionData?.partisan;
  const [ races, setRaces ] = useState(null);
  const [ numRaces, setNumRaces ] = useState(0);
  const parsedQuery = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );
  const filters = useMemo(() => parseRaceFiltersFromQuery(parsedQuery), [parsedQuery]);
  const filtersPrev = usePrevious(filters);
  const [ loading, setLoading ] = useState(false);
  const [ loadingTimestamps, setLoadingTimestamps ] = useState([]);  // Track loading timestamps
  const [ queuedResults, setQueuedResults ] = useState(null);  // Queue results for processing
  const [ countyOptions, setCountyOptions ] = useState([]);
  const [ cityOptions, setCityOptions ] = useState([]);
  const [ officeOptions, setOfficeOptions ] = useState([]);
  const [ partyOptions, setPartyOptions ] = useState([]);
  const numRacesPerPage = 10;
  const [ moreMenuAnchorEl, setMoreMenuAnchorEl ] = useState(null);
  const [ exportCandidateModalOpen, setExportCandidateModalOpen ] = useState(false);
  const [ searchFocusNonce, setSearchFocusNonce ] = useState(0);
  const [ moreFiltersMenuAnchorEl, setMoreFiltersMenuAnchorEl ] = useState(null);
  const [ electionImplementation, setElectionImplementation ] = useState(null);
  const [ cancellingProcessInProgress, setCancellingProcessInProgress ] = useState(false);

  const qualifyingPassed = electionData?.qualifyingDate
    ? moment().isAfter(moment.utc(electionData.qualifyingDate).add(1, 'day'))
    : true;

  const refreshElectionImplementation = useCallback(async () => {
    try {
      const eiData = await feathers.getService('election-implementations').get(election);
      setElectionImplementation(eiData);
      return eiData;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [feathers, election]);

  const cancelProcessInProgress = async () => {
    if (!feathers || cancellingProcessInProgress) return;

    setCancellingProcessInProgress(true);
    try {
      const eiData = await feathers.getService('election-implementations').patch(election, {
        processInProgress: null,
      });
      setElectionImplementation(eiData);
      setRaces(null);
    } catch (err) {
      console.error('Error cancelling election implementation process:', err);
    } finally {
      setCancellingProcessInProgress(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'f') return;
      const target = e.target;
      const tag = target?.tagName?.toLowerCase();
      const editable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      setSearchFocusNonce((n) => n + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  /* handle filter controls for data (page, sort, filters from URL) */
  const sortByStr = parsedQuery.sortBy;
  const activePage = parsedQuery.page ? parseInt(parsedQuery.page, 10) : 0;
  const sortFilters = useMemo(() => {
    try {
      return sortByStr ? JSON.parse(sortByStr) : [];
    } catch {
      return [];
    }
  }, [sortByStr]);

  const activePagePrev = usePrevious(activePage);
  const sortByPrev = usePrevious(sortByStr);

  const descendantOf = useMemo(() => {
    return electionData?.districts
  }, [electionData])
  const stateId = useMemo(() => (
    electionData?.state || null
  ), [electionData]);
  const partyKeys = useMemo(() => {
    const partiesPresent = Array.isArray(electionData?.partiesPresent)
      ? electionData.partiesPresent
      : [];

    return partiesPresent
      .map((party) => {
        return typeof party === 'string' ? party : party?.key;
      })
      .filter(Boolean)
      .filter((party, idx, parties) => parties.indexOf(party) === idx);
  }, [electionData?.partiesPresent]);
  const partyNameByKey = useMemo(() => (
    Object.fromEntries(partyOptions.map((party) => [party.value, party.label]))
  ), [partyOptions]);

  useEffect(() => {
    const loadPartyOptions = async () => {
      if (!feathers || partyKeys.length === 0) {
        setPartyOptions([]);
        return;
      }

      const partiesResponse = await feathers.getService('parties').find({
        query: {
          key: { $in: partyKeys },
          $limit: partyKeys.length,
        },
      });
      const parties = Array.isArray(partiesResponse) ? partiesResponse : (partiesResponse?.data || []);
      const partiesByKey = Object.fromEntries(parties.map((party) => [party.key, party]));

      setPartyOptions(partyKeys.map((key) => ({
        label: partiesByKey[key]?.name || key,
        value: key,
      })));
    };

    loadPartyOptions();
  }, [feathers, partyKeys]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!feathers) return;
      if (!stateId) {
        setCountyOptions([]);
        setCityOptions([]);
        return;
      }

      const districtQueryBase = {
        parentId: stateId,
        $sort: { name: 1 },
      };

      const [counties, cities] = await Promise.all([
        loadPaginatedFrontend(
          'districts',
          { ...districtQueryBase, type: 'county' },
          feathers,
          600,
        ),
        loadPaginatedFrontend(
          'districts',
          { ...districtQueryBase, type: 'city' },
          feathers,
          600,
        ),
      ]);
      setCountyOptions(
        (counties || [])
          .map((district) => ({
            label: district.longName,
            value: district._id,
          })),
      );
      setCityOptions(
        (cities || [])
          .map((district) => ({
            label: district.longName,
            value: district._id,
          })),
      );

      const officesResponse = await feathers.getService('offices').find({
        query: {
          ...(descendantOf ? { district: { $within: descendantOf } } : {}),
          $limit: 1000,
          $sort: { name: 1 },
        },
      });
      const uniqueOfficeOptions = (officesResponse?.data || []).reduce((acc, office) => {
        if (acc.some((option) => option.value === office.key)) return acc;
        return acc.concat({
          label: office.name,
          value: office.key,
        });
      }, []);
      setOfficeOptions(uniqueOfficeOptions);
    };

    loadFilterOptions();
  }, [feathers, stateId]);

  const updateFilter = (key, value) => {
    let nextFilters = { ...filters };
    if (key === 'county') {
      nextFilters = { ...nextFilters, county: value, city: null };
    } else if (key === 'city') {
      nextFilters = { ...nextFilters, county: null, city: value };
    } else {
      nextFilters = { ...nextFilters, [key]: value };
    }
    const newQs = buildRacesUrlQuery({
      page: activePage,
      sortFilters,
      filters: nextFilters,
    });
    history.replace(`${location.pathname}${newQs ? `?${newQs}` : ''}`);
  };

  const updatePageQuery = (page, sortByArg) => {
    const newQs = buildRacesUrlQuery({
      page,
      sortFilters: sortByArg,
      filters,
    });
    history.push(`${location.pathname}${newQs ? `?${newQs}` : ''}`);
  };

  const sortByField = (fieldName) => {
    const existingFilterForField = (sortFilters || []).some(s => s.field === fieldName);
    console.log(`existingFilterForField`, existingFilterForField)
    const sortFiltersNew = (sortFilters || []).map(s => {
      if(s.field === fieldName) {
        return { field: fieldName, ascending: s?.ascending === 1 ? -1 : 0 };
      } else {
        return s;
      }
    }).filter(v => v?.ascending !== 0);
    console.log(`sortFiltersNew`, sortFiltersNew)
    const sortFiltersFinal = existingFilterForField ? sortFiltersNew.slice() : [].concat(sortFiltersNew, { field: fieldName, ascending: 1 });
    updatePageQuery(0, sortFiltersFinal)
  }

  const getRaces = async (election, page = 0, activeFilters, sortFilters) => {
    // Get current timestamp
    const timestamp = Date.now();
    setLoadingTimestamps(l => [...l, timestamp]);
    setLoading(true);

    const filterQueries = {};
    const term = activeFilters?.search || '';
    if (activeFilters?.city) {
      filterQueries.cities = activeFilters.city;
    } else if (activeFilters?.county) {
      filterQueries.counties = activeFilters.county;
    }
    if (activeFilters?.office) filterQueries.office = activeFilters.office;
    if (activeFilters?.coveragePlan) filterQueries.coveragePlan = activeFilters.coveragePlan;
    if (activeFilters?.priorityLevel) filterQueries.priorityLevel = activeFilters.priorityLevel;
    if (activeFilters?.party) filterQueries.party = activeFilters.party;
    if (activeFilters?.runoff !== null) filterQueries['resultSummary.goingToRunoff'] = activeFilters.runoff;
    if (activeFilters?.uncontestedDetailed) {
      filterQueries.uncontestedDetailed = activeFilters.uncontestedDetailed;
    }
    if (activeFilters?.workflow) {
      if (activeFilters.workflow === 'needsReview') {
        filterQueries.synced = false;
      } else {
        const [workflowKey, workflowStatus] = activeFilters.workflow.split(':');
        filterQueries.synced = false;
        filterQueries[`workflows.${workflowKey}`] = workflowStatus;
      }
    }

    const $sort = sortFilters?.length > 0
      ? sortFilters.reduce((acc, val) => {
          const update = {};
          update[val.field] = val.ascending;
          return { ...acc, ...update}
        }, {})
      : { ballotOrder: 1, expectedReaders: -1, createdAt: 1 };
    const query = {
      election,
      $skip: page*numRacesPerPage,
      $limit: numRacesPerPage,
      ...(term
        ? { $or: [ { candidatesSearchTerm: { $search: term } }, { officeSearchTerm: { $search: term } } ] }
        : {}
      ),
      $sort,
      ...filterQueries
    }
    const races = await feathers.getService('races').find({ query });
    
    // Queue results with timestamp instead of setting state directly
    setQueuedResults({
      timestamp,
      data: races?.data || [],
      total: races?.total
    });
  }

  // Add new useEffect to handle queued results
  useEffect(() => {
    if (queuedResults) {
      setQueuedResults(null);

      const mostRecentTimestamp = loadingTimestamps[loadingTimestamps.length - 1];
      const isMostRecentResult = mostRecentTimestamp === queuedResults.timestamp;
      
      // Only process the most recent results
      if (!isMostRecentResult) return;

      setRaces(queuedResults.data);
      setNumRaces(queuedResults.total);
      setLoading(false);
    }
  }, [queuedResults, loadingTimestamps]);

  useEffect(() => {
    if (feathers) {
      refreshElectionImplementation();
    }
  }, [feathers, refreshElectionImplementation]);

  useEffect(() => {
    const processInProgress = electionImplementation?.processInProgress;
    if (!processInProgress || !feathers) return;

    const startedAt = processInProgress.startedAt ? new Date(processInProgress.startedAt).getTime() : Date.now();
    const elapsed = () => Date.now() - startedAt;
    if (elapsed() >= PROCESS_MAX_DURATION_MS) return;

    const interval = setInterval(async () => {
      if (elapsed() >= PROCESS_MAX_DURATION_MS) {
        clearInterval(interval);
        return;
      }
      const latest = await refreshElectionImplementation();
      if (!latest?.processInProgress) {
        clearInterval(interval);
        setRaces(null);
      }
    }, PROCESS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [
    electionImplementation?.processInProgress?.process,
    electionImplementation?.processInProgress?.startedAt,
    electionImplementation?.processInProgress,
    feathers,
    refreshElectionImplementation
  ]);

  useEffect(() => {
    if(feathers) {
      if(races === null && !loading) {
        getRaces(election, activePage, filters, sortFilters)
      } else if(activePage !== activePagePrev ||
        JSON.stringify(filtersPrev || {}) !== JSON.stringify(filters) ||
        sortByPrev !== sortByStr
      ) {
        getRaces(election, activePage, filters, sortFilters)
      }
    }
  }, [feathers, activePage, election, filters, races, loading, sortByStr]);

  const processInProgress = electionImplementation?.processInProgress;
  const processInProgressStatus = processInProgress?.process === 'reordering-races'
    ? 'Reordering races...'
    : processInProgress?.process
      ? `Processing: ${processInProgress.process}`
      : 'Processing...';
  const processInProgressTooltip = `${processInProgressStatus} Click to clear this status if it is stuck.`;

  return (
    <Wrapper>
      {
        route.path.includes('/new') &&
        (
          <Modal onClose={() => history.goBack()}>
            <CreateRaceModal
              onClose={() => history.goBack()}
              election={electionData}
              electionKey={election}
              redirectOnFinish={true}
            />
          </Modal>
        )
      }
      <DataDetailToolbar
        onBack={{ to: {
            pathname: `/elections/${election}`
          }}}
        navTree={[
          {
            text: 'Elections',
            to: {
              pathname: `/elections`,
            }
          },
          {
            text: electionData?.name || 'Election',
            to: {
              pathname: `/elections/${election}`,
            }
          },
          { text: 'Races' }
        ]}
        actionButtonsComponent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {processInProgress && (
              <Tooltip title={processInProgressTooltip}>
                <IconButton
                  onClick={cancelProcessInProgress}
                  size="small"
                  disabled={cancellingProcessInProgress}
                >
                  <CircularProgress size={24} />
                </IconButton>
              </Tooltip>
            )}
            {
              isPermitted(user, ['editor', 'admin', 'researcher', 'super-admin', 'reviewer', 'publisher']) &&
              <IconButton
                onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
                size="small"
              >
                <MoreVert />
              </IconButton>
            }
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={() => setMoreMenuAnchorEl(null)}
            >
              <MenuItem 
                component={Link} 
                to={`/elections/${election}/candidate-lists`}
                onClick={() => setMoreMenuAnchorEl(null)}
              >
                Upload candidate lists (CSV)
              </MenuItem>
              {
                isPermitted(user, ['super-admin', 'editor']) &&
                <MenuItem
                  component={Link}
                  to={`/elections/${election}/races/reorder`}
                  onClick={() => setMoreMenuAnchorEl(null)}
                >
                  Reorder races
                </MenuItem>
              }
              {
                isPermitted(user, ['super-admin', 'editor']) &&
                <MenuItem 
                  onClick={() => {
                    setExportCandidateModalOpen(true);
                    setMoreMenuAnchorEl(null);
                  }}
                >
                  Export Candidate Data
                </MenuItem>
              }
            </Menu>
            {
              isPermitted(user, ['editor', 'admin', 'researcher', 'super-admin', 'reviewer', 'publisher'])
              ? <CTAButton onClick={{ to: 'races/new' }} value='New' mode='pill'/>
              : <div/>
            }
          </div>
        }
      />
      <DownloadData
        service="candidates"
        serviceDisplay="Candidates"
        defaultQuery={{ election }}
        open={exportCandidateModalOpen}
        onOpenChange={setExportCandidateModalOpen}
        hideButton={true}
        transformData={async (candidates) => {
          const raceIds = [...new Set(candidates.map(candidate => candidate.race).filter(Boolean))];
          const raceIdChunks = Array.from(
            { length: Math.ceil(raceIds.length / EXPORT_RACE_QUERY_CHUNK_SIZE) },
            (_, index) => raceIds.slice(index * EXPORT_RACE_QUERY_CHUNK_SIZE, (index + 1) * EXPORT_RACE_QUERY_CHUNK_SIZE)
          );
          const racesForCandidates = raceIdChunks.length > 0
            ? (await asyncPoolAll(5, raceIdChunks, async (raceIdChunk) => {
              const response = await feathers.getService('races').find({
                query: {
                  _id: raceIdChunk,
                  $limit: raceIdChunk.length,
                },
              });
              return response.data || [];
            })).flat()
            : [];
          const raceById = racesForCandidates.reduce((acc, race) => {
            acc[race._id] = race;
            return acc;
          }, {});

          return candidates.map(candidate => {
            // Extract race data
            const raceObj = raceById[candidate.race];
            const district = raceObj?.district || raceObj?.originalDistrict;
            const districtObj = typeof district === 'object' ? district : null;
            const office = raceObj?.office || '';

            // Extract contact info - first 2 phones and first 2 emails
            const contactPhones = (candidate.contact || [])
              .filter(c => c.method === 'phone')
              .slice(0, 2)
              .map(c => c.value || '');
            const contactEmails = (candidate.contact || [])
              .filter(c => c.method === 'email')
              .slice(0, 2)
              .map(c => c.value || '');

            // Extract references - social media and website
            const references = candidate.stagingDraft?.references || {};
            const categories = references.categories || [];
            
            // Find website
            const websiteCategory = categories.find(c => c.type === 'website');
            const candidateWebsite = websiteCategory?.sources?.[0]?.url || '';

            // Find social media
            const socialCategory = categories.find(c => c.type === 'social');
            const socialSources = socialCategory?.sources || [];
            const facebookUrl = socialSources.find(s => s?.mediaType === 'facebook')?.url || '';
            const twitterUrl = socialSources.find(s => s?.mediaType === 'twitter')?.url || '';
            const instagramUrl = socialSources.find(s => s?.mediaType === 'instagram')?.url || '';
            const youtubeUrl = socialSources.find(s => s?.mediaType === 'youtube')?.url || '';
            const tiktokUrl = socialSources.find(s => s?.mediaType === 'tiktok')?.url || '';
            const linkedinUrl = socialSources.find(s => s?.mediaType === 'linkedin')?.url || '';
            const blueskyUrl = socialSources.find(s => s?.mediaType === 'bluesky')?.url || '';
            const threadsUrl = socialSources.find(s => s?.mediaType === 'threads')?.url || '';

            // Extract internalInfo
            const internalInfo = candidate.internalInfo || {};
            const address = (internalInfo.address || [])[0] || '';
            const rawOccupation = internalInfo.occupation;
            const occupation = Array.isArray(rawOccupation)
              ? (rawOccupation[0] || '')
              : (typeof rawOccupation === 'string' ? rawOccupation : '');

            // Get party name
            const partyKey = candidate.party || '';
            const partyName = partyKey ? (partyNameByKey[partyKey] || partyKey) : '';

            // Prettify uncontestedDetailed status
            const uncontestedDetailed = raceObj?.uncontestedDetailed || '';
            const prettifiedUncontestedStatus = uncontestedDetailed
              ? uncontestedDetailed
                  .split('-')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : '';

            const exportRow = {
              'Candidate Name': candidate.name || '',
              'Party': partyName,
              'Candidate ID': candidate._id || '',
              'Branch Officeholder ID': candidate.official || '',
              'District Match Name': districtObj?.matchName || '',
              'District Name': districtObj?.longName || '',
              'Race Key': raceObj?.raceKey || '',
              'Race Name': raceObj?.longName || raceObj?.officeName || '',
              'Uncontested Status': prettifiedUncontestedStatus,
              'Office Key': office || '',
              'Address': address,
              'Occupation': occupation,
              'Phone 1': contactPhones[0] || '',
              'Phone 2': contactPhones[1] || '',
              'Email 1': contactEmails[0] || '',
              'Email 2': contactEmails[1] || '',
              'Facebook URL': facebookUrl,
              'Twitter URL': twitterUrl,
              'Instagram URL': instagramUrl,
              'YouTube URL': youtubeUrl,
              'TikTok URL': tiktokUrl,
              'LinkedIn URL': linkedinUrl,
              'Bluesky URL': blueskyUrl,
              'Threads URL': threadsUrl,
              'Candidate Website': candidateWebsite
            };
            return exportRow;
          });
        }}
      />
      <FilterBar>
        <FilterRow>
          <FilterButton
            label='County'
            value={filters.county}
            onChange={(value) => updateFilter('county', value)}
            options={countyOptions}
            enableOptionSearch
          />
          <FilterButton
            label='City'
            value={filters.city}
            onChange={(value) => updateFilter('city', value)}
            options={cityOptions}
            enableOptionSearch
          />
          <FilterButton
            label='Office'
            value={filters.office}
            onChange={(value) => updateFilter('office', value)}
            options={officeOptions}
            enableOptionSearch
          />
          <FilterButton
            label='Coverage plan'
            value={filters.coveragePlan}
            onChange={(value) => updateFilter('coveragePlan', value)}
            options={[
              { label: 'No coverage', value: 'no-coverage' },
              { label: 'Basic coverage', value: 'basic-coverage' },
              { label: 'Auto coverage', value: 'auto-coverage' },
            ]}
          />
          <FilterButton
            label='Priority'
            value={filters.priorityLevel}
            onChange={(value) => updateFilter('priorityLevel', value)}
            options={[
              { label: 'High', value: 'high' },
              { label: 'Medium', value: 'medium' },
              { label: 'Low', value: 'low' },
            ]}
          />
          {isPartisan && partyOptions.length > 0 && (
            <FilterButton
              label='Party'
              value={filters.party}
              onChange={(value) => updateFilter('party', value)}
              options={partyOptions}
            />
          )}
          <FilterButton
            label='Status'
            value={filters.workflow}
            onChange={(value) => updateFilter('workflow', value)}
            options={[
              { label: 'Draft', value: 'draft:ready' },
              { label: 'References added', value: 'referencesAdded:ready' },
              { label: 'Auto profile failed', value: 'autoProfileFailed:ready' },
              { label: 'Auto profile created', value: 'autoProfileCreated:ready' },
              { label: 'Auto profile reviewed', value: 'autoProfileReviewed:ready' },
              { label: 'Published', value: 'published:ready' },
              { label: 'Unpublished changes', value: 'needsReview' },
            ]}
          />
          {filters.runoff !== null && (
            <FilterButton
              label='Runoff'
              value={filters.runoff}
              onChange={(value) => updateFilter('runoff', value)}
              options={[
                { label: 'Going to runoff', value: true },
                { label: 'Not going to runoff', value: false },
              ]}
            />
          )}
          {filters.uncontestedDetailed !== null && (
            <FilterButton
              label='Competitive'
              value={filters.uncontestedDetailed}
              onChange={(value) => updateFilter('uncontestedDetailed', value)}
              options={[
                { label: 'Contested', value: 'contested' },
                { label: 'Uncontested', value: 'uncontested' },
                { label: 'Under-contested', value: 'under-contested' },
              ]}
            />
          )}
          <Button
            variant={
              filters.runoff !== null || filters.uncontestedDetailed !== null
                ? 'contained'
                : 'outlined'
            }
            color='inherit'
            size='small'
            onClick={(e) => setMoreFiltersMenuAnchorEl(e.currentTarget)}
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              fontWeight: 400,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'divider',
              ...(filters.runoff !== null || filters.uncontestedDetailed !== null
                ? {}
                : { bgcolor: 'background.paper' }),
            }}
          >
            + More
          </Button>
          <Menu
            anchorEl={moreFiltersMenuAnchorEl}
            open={Boolean(moreFiltersMenuAnchorEl)}
            onClose={() => setMoreFiltersMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <ListSubheader sx={{ fontWeight: 600, lineHeight: '36px' }}>Runoff</ListSubheader>
            <MenuItem
              selected={filters.runoff === true}
              onClick={() => {
                updateFilter('runoff', true);
                setMoreFiltersMenuAnchorEl(null);
              }}
            >
              Going to runoff
            </MenuItem>
            <MenuItem
              selected={filters.runoff === false}
              onClick={() => {
                updateFilter('runoff', false);
                setMoreFiltersMenuAnchorEl(null);
              }}
            >
              Not going to runoff
            </MenuItem>
            {filters.runoff !== null && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    updateFilter('runoff', null);
                    setMoreFiltersMenuAnchorEl(null);
                  }}
                >
                  Clear runoff filter
                </MenuItem>
              </>
            )}
            <Divider />
            <ListSubheader sx={{ fontWeight: 600, lineHeight: '36px' }}>Competitive</ListSubheader>
            <MenuItem
              selected={filters.uncontestedDetailed === 'contested'}
              onClick={() => {
                updateFilter('uncontestedDetailed', 'contested');
                setMoreFiltersMenuAnchorEl(null);
              }}
            >
              Contested
            </MenuItem>
            <MenuItem
              selected={filters.uncontestedDetailed === 'uncontested'}
              onClick={() => {
                updateFilter('uncontestedDetailed', 'uncontested');
                setMoreFiltersMenuAnchorEl(null);
              }}
            >
              Uncontested
            </MenuItem>
            <MenuItem
              selected={filters.uncontestedDetailed === 'under-contested'}
              onClick={() => {
                updateFilter('uncontestedDetailed', 'under-contested');
                setMoreFiltersMenuAnchorEl(null);
              }}
            >
              Under-contested
            </MenuItem>
            {filters.uncontestedDetailed !== null && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    updateFilter('uncontestedDetailed', null);
                    setMoreFiltersMenuAnchorEl(null);
                  }}
                >
                  Clear competitive filter
                </MenuItem>
              </>
            )}
          </Menu>
        </FilterRow>
        <RaceSearchSlot>
          <FilterButton
            label='Search'
            value={filters.search}
            onChange={(value) => updateFilter('search', value || '')}
            isSearch
            onSearch={(term) => updateFilter('search', term)}
            focusNonce={searchFocusNonce}
          />
        </RaceSearchSlot>
      </FilterBar>

      <HeaderRow>
        <Typography variant='h5'>Title</Typography>
        <Typography variant='h5'>Candidates</Typography>
        {
          <HeaderRowItem
            sortFieldName='informationLevel'
            sortFilters={sortFilters}
            sortFunction={sortByField}
          >
            Status
          </HeaderRowItem>
        }
        <HeaderRowItem
          sortFieldName='expectedReaders'
          sortFilters={sortFilters}
          sortFunction={sortByField}
        >
          Expected Readers
        </HeaderRowItem>
        <HeaderRowItem
          sortFieldName='analytics.views'
          sortFilters={sortFilters}
          sortFunction={sortByField}
        >
          # Views
        </HeaderRowItem>
        <Typography variant='h5'></Typography>
      </HeaderRow>
      <Content>

        {
          loading
          ?
            <LoadingTile>
              <span>Searching races...</span>
              <LoadingSpinner scale={0.8}/>
            </LoadingTile>
          :
            (races || []).map((race, idx) => {
              const noCoverage = race.coveragePlan === 'no-coverage';
              return (
              <RacePreviewTile
                qualifyingPassed={qualifyingPassed}
                party={isPartisan ? race.party : null}
                title={race.officeName}
                retention={race.retention}
                resultSummary={race.resultSummary}
                showResultsLinked={(electionData?.status || '').includes('results')}
                synced={noCoverage ? true : race.synced}
                coverageStatus={noCoverage ? 'coverage' : race.coverageStatus}
                willCover={race.willCover}
                updatedAt={race.updatedAt}
                office={race.office.name}
                district={raceDistrictDisplayText(race)}
                expectedReaders={race?.expectedReaders}
                priorityLevel={race?.priorityLevel}
                numVoters={race.analytics?.onBallot ?? 0}
                numViews={race.analytics?.views ?? 0}
                progress={0.2}
                to={{
                  pathname: `races/${race._id}`,
                  ...( location.search?.length > 0
                    ? { state: { racesSearch: location.search }}
                    : {}
                  )
                }}
                candidateSummary={race.candidateSummary}
                key={race._id}
              />
              );
            })
        }
      </Content>
      <Row>
        <PaginationStatus
          activePage={activePage + 1}
          items={numRaces}
          itemsPerPage={numRacesPerPage}
          itemName='race'
        />
        <Pagination
          count={Math.ceil(numRaces / numRacesPerPage)}
          page={activePage + 1}
          onChange={(event, newPage) => updatePageQuery(newPage - 1, sortFilters)}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Row>
    </Wrapper>
  )
}

const HeaderRow = styled.div`
  width: calc(100% - 24px * 2);
  margin: 12px 24px 4px;
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 70px 70px 25px;
  grid-gap: 24px;
  align-items: end;
`

const HeaderRowItem = ({
  sortFieldName,
  sortFilters,
  sortFunction,
  children
}) => {
  const active = (sortFilters || []).some(f => f?.field === sortFieldName);
  const ascending = active && sortFilters.filter(f => f?.field === sortFieldName)[0].ascending === 1;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        ...(sortFunction ? { cursor: 'pointer' } : {}),
      }}
      {...(sortFunction ? { onClick: () => sortFunction(sortFieldName) } : {}) }
    >
      <Typography
        variant='h5'
        style={{
          ...(active ? { color: '#453DB7' } : {})
        }}
      >
        {children}
      </Typography>
      {
        active &&
        (ascending ? <ArrowUp style={{ height: '16px', color: '#453DB7' }}/> : <ArrowDown style={{ height: '16px', color: '#453DB7' }}/>)
      }
    </div>
  )

}

export default Races;
